import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useApp } from "@/contexts/AppContext";
import { User, MessageSquare, Bell, Check, X, SlidersHorizontal, Store, Eye, UserPlus } from "lucide-react";
import BottomNav from "@/components/BottomNav";
import DailyRewards from "@/components/DailyRewards";
import PageTransition from "@/components/PageTransition";
import LiveCounter from "@/components/LiveCounter";
import FilterPanel from "@/components/FilterPanel";
import coinBag from "@/assets/coin-bag.png";
import { supabase } from "@/integrations/supabase/client";
import GuestUpgradePrompt from "@/components/GuestUpgradePrompt";
import SEO from "@/components/SEO";

type Visitor = { id: string; viewerId: string; username: string; profilePicture?: string; at: Date };


const HomePage = () => {
  const { user, friendRequests, acceptFriendRequest, ignoreFriendRequest, getOrCreateConversation, setActiveConversation, sendFriendRequest } = useApp();
  const navigate = useNavigate();
  const [showRequests, setShowRequests] = useState(false);
  const [friendAccepted, setFriendAccepted] = useState<{ username: string; fromUserId: string } | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filterGenders, setFilterGenders] = useState<string[]>(["All"]);
  const [filterContinent, setFilterContinent] = useState<string>("Global");
  const [visitors, setVisitors] = useState<Visitor[]>([]);
  const [showVisitors, setShowVisitors] = useState(false);
  const [guestFriendPrompt, setGuestFriendPrompt] = useState<{ username: string } | null>(null);

  // Profile-view notifications (Pro-only). Loads recent visitors and listens for new ones in realtime.
  useEffect(() => {
    if (!user?.isPro) return;
    let cancelled = false;

    const fetchVisitor = async (row: { id: string; viewer_id: string; created_at: string }) => {
      const { data: p } = await (supabase
        .from("public_profiles" as any)
        .select("user_id, username, profile_picture")
        .eq("user_id", row.viewer_id)
        .maybeSingle() as any);
      if (!p) return null;
      return {
        id: row.id,
        viewerId: (p as any).user_id,
        username: (p as any).username,
        profilePicture: (p as any).profile_picture || undefined,
        at: new Date(row.created_at),
      } as Visitor;
    };

    const fetchVisitorsBatch = async (rows: { id: string; viewer_id: string; created_at: string }[]) => {
      const ids = Array.from(new Set(rows.map((r) => r.viewer_id)));
      if (ids.length === 0) return [];
      const { data: profs } = await (supabase
        .from("public_profiles" as any)
        .select("user_id, username, profile_picture")
        .in("user_id", ids) as any);
      const byId = new Map<string, any>((profs || []).map((p: any) => [p.user_id, p]));
      return rows
        .map((row) => {
          const p = byId.get(row.viewer_id);
          if (!p) return null;
          return {
            id: row.id,
            viewerId: p.user_id,
            username: p.username,
            profilePicture: p.profile_picture || undefined,
            at: new Date(row.created_at),
          } as Visitor;
        })
        .filter(Boolean) as Visitor[];
    };

    (async () => {
      const { data } = await (supabase
        .from("profile_views" as any)
        .select("id, viewer_id, created_at")
        .eq("viewed_id", user.id)
        .order("created_at", { ascending: false })
        .limit(15) as any);
      if (cancelled || !data) return;
      const resolved = await fetchVisitorsBatch(data as any[]);
      if (!cancelled) setVisitors(resolved);
    })();

    const channel = supabase
      .channel("profile-views-notify")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "profile_views", filter: `viewed_id=eq.${user.id}` },
        async (payload) => {
          const v = await fetchVisitor(payload.new as any);
          if (v) setVisitors((prev) => [v, ...prev.filter((x) => x.viewerId !== v.viewerId)].slice(0, 15));
        }
      )
      .subscribe();

    return () => { cancelled = true; supabase.removeChannel(channel); };
  }, [user?.id, user?.isPro]);

  if (!user) return null;

  const handleAcceptFriend = async (reqId: string, username: string, fromUserId: string) => {
    if (user?.isAnonymous) {
      setGuestFriendPrompt({ username });
      return;
    }
    await acceptFriendRequest(reqId);
    setFriendAccepted({ username, fromUserId });
  };

  const handleViewVisitorProfile = async (v: Visitor) => {
    setShowVisitors(false);
    try {
      const convId = await getOrCreateConversation(v.viewerId);
      setActiveConversation(convId);
      navigate("/messages");
    } catch { /* ignore */ }
  };

  const handleAddVisitorFriend = async (v: Visitor) => {
    try { await sendFriendRequest(v.username); } catch { /* ignore */ }
  };

  const handleTapToChat = async () => {
    if (!friendAccepted) return;
    try {
      const convId = await getOrCreateConversation(friendAccepted.fromUserId);
      setActiveConversation(convId);
      navigate("/messages");
    } catch {
      navigate("/messages");
    }
    setFriendAccepted(null);
  };

  return (
    <PageTransition>
      <SEO
        title="Home — TalkLot"
        description="Your TalkLot home. Start a random chat, open recent conversations, claim daily rewards, and manage friend requests."
        path="/"
      />
      <div className="h-[100dvh] bg-background flex flex-col pb-16 relative overflow-y-auto">
        {/* Ambient */}
        <div className="absolute top-0 right-0 w-80 h-80 rounded-full bg-primary/[0.04] blur-[100px] pointer-events-none" />
        <div className="absolute bottom-32 left-0 w-64 h-64 rounded-full bg-accent/[0.03] blur-[80px] pointer-events-none" />

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-2 relative z-10">
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => navigate("/settings")}
            className="w-10 h-10 rounded-full bg-muted/60 flex items-center justify-center border border-border/30 overflow-hidden"
          >
            {user.profilePicture ? (
              <img loading="lazy" decoding="async" src={user.profilePicture} className="w-full h-full rounded-full object-cover" alt="Your profile picture" />
            ) : (
              <User className="w-4 h-4 text-muted-foreground" />
            )}
          </motion.button>


          <div className="flex items-center gap-3">
            {user.isPro && (
              <motion.button whileTap={{ scale: 0.95 }} onClick={() => setShowVisitors(true)} className="relative" aria-label="Profile visits">
                <Eye className="w-5 h-5 text-muted-foreground" />
                {visitors.length > 0 && (
                  <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }}
                    className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full gradient-premium text-[9px] font-bold text-primary-foreground flex items-center justify-center">
                    {visitors.length}
                  </motion.span>
                )}
              </motion.button>
            )}
            <motion.button whileTap={{ scale: 0.95 }} onClick={() => setShowRequests(true)} className="relative" aria-label="Friend requests">
              <Bell className="w-5 h-5 text-muted-foreground" />
              {friendRequests.length > 0 && (
                <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }}
                  className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-destructive text-[9px] font-bold text-destructive-foreground flex items-center justify-center shadow-[0_0_6px_hsl(var(--destructive)/0.6)]">
                  {friendRequests.length}
                </motion.span>
              )}
            </motion.button>

            <motion.button whileTap={{ scale: 0.97 }} onClick={() => navigate("/shop")}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted/40 border border-border/20"
              aria-label="Coin balance — open shop">
              <img loading="lazy" decoding="async" src={coinBag} alt="Currency balance" className="w-7 h-7 object-contain" />
              <span className="font-display font-semibold text-foreground text-xs">{user.coins}</span>
            </motion.button>

            <motion.button
              whileTap={{ scale: 0.92 }}
              onClick={() => navigate("/themes")}
              aria-label="Theme Shop"
              className="relative w-11 h-11 rounded-full gradient-premium border border-primary/40 flex items-center justify-center shadow-[0_0_20px_-6px_hsl(var(--primary)/0.7)]"
            >
              <Store className="w-5 h-5 text-primary-foreground" strokeWidth={2.4} />
              <span className="absolute inset-0 rounded-full bg-primary/20 blur-md -z-10" />
            </motion.button>
          </div>
        </div>

        <DailyRewards />

        {/* Main Content */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 gap-6 relative z-10 -mt-4">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-center flex flex-col items-center gap-2.5">
            <h1 className="text-2xl font-display font-bold text-foreground mb-0">
              Hey, <span className="text-gradient-premium">{user.username}</span>
            </h1>
            <LiveCounter />
          </motion.div>

          {/* PRIMARY CTA — text chat */}
          <motion.div className="relative" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1 }}>
            <div className="absolute inset-0 rounded-full bg-primary/10 blur-2xl scale-125 animate-pulse-glow" />
            <motion.button whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.02 }} onClick={() => navigate("/random-chat")}
              className="relative w-40 h-40 rounded-full gradient-premium flex flex-col items-center justify-center gap-1.5 transition-all overflow-hidden shadow-2xl">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.07] to-transparent animate-shimmer" style={{ backgroundSize: "200% 100%" }} />
              <MessageSquare className="w-9 h-9 text-primary-foreground relative z-10" />
              <span className="font-display font-bold text-primary-foreground text-base relative z-10">Find Someone</span>
            </motion.button>
          </motion.div>

          <motion.button initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} whileTap={{ scale: 0.97 }}
            onClick={() => navigate("/messages")}
            className="relative w-full max-w-[240px] min-h-[48px] py-3.5 px-6 rounded-2xl gradient-accent flex items-center justify-center gap-2 transition-all overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.06] to-transparent animate-shimmer" style={{ backgroundSize: "200% 100%" }} />
            <MessageSquare className="w-5 h-5 text-accent-foreground relative z-10" />
            <span className="font-display font-semibold text-accent-foreground text-sm relative z-10">My Chats</span>
          </motion.button>

          {/* Filters — visible, Pro-locked inside */}
          <motion.button
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
            whileTap={{ scale: 0.97 }} onClick={() => setShowFilters(true)}
            className="flex items-center gap-1.5 text-[11px] text-foreground/80 px-3.5 py-2 rounded-full bg-muted/30 border border-border/20"
          >
            <SlidersHorizontal className="w-3 h-3 text-primary" />
            <span className="font-medium">Filters</span>
            {!user.isPro && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-accent/20 text-accent font-semibold ml-1">PRO</span>
            )}
          </motion.button>
        </div>

        <FilterPanel
          isOpen={showFilters}
          onClose={() => setShowFilters(false)}
          onApply={(g, c) => { setFilterGenders(g); setFilterContinent(c); }}
          initialGenders={filterGenders}
          initialCountry={filterContinent}
        />

        {/* Friend Requests Modal */}
        <AnimatePresence>
          {showRequests && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100] flex items-end justify-center bg-black/70 backdrop-blur-sm" onClick={() => setShowRequests(false)}>
              <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
                transition={{ type: "spring", damping: 30, stiffness: 350 }} onClick={(e) => e.stopPropagation()}
                className="w-full max-w-lg rounded-t-3xl bg-card border-t border-border/40 p-6 max-h-[70vh] overflow-y-auto">
                <div className="w-10 h-1 rounded-full bg-muted-foreground/20 mx-auto mb-5" />
                <div className="flex items-center justify-between mb-5">
                  <h3 className="font-display font-bold text-foreground text-lg">Requests</h3>
                  <button onClick={() => setShowRequests(false)}><X className="w-5 h-5 text-muted-foreground" /></button>
                </div>
                {friendRequests.length === 0 ? (
                  <div className="text-center py-10"><p className="text-muted-foreground text-sm">No pending requests</p></div>
                ) : (
                  <div className="space-y-2">
                    {friendRequests.map((req) => (
                      <motion.div key={req.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                        className="flex items-center justify-between p-3 rounded-2xl bg-muted/30 border border-border/20">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-muted/60 flex items-center justify-center relative">
                            <User className="w-4 h-4 text-muted-foreground" />
                            {req.isOnline && <span className="absolute bottom-0 right-0 w-2 h-2 rounded-full bg-emerald-400 border-2 border-card" />}
                          </div>
                          <span className="font-display font-semibold text-foreground text-sm">{req.username}</span>
                        </div>
                        <div className="flex gap-1.5">
                          {user.isAnonymous ? (
                            <motion.button
                              whileTap={{ scale: 0.95 }}
                              onClick={() => { setShowRequests(false); setGuestFriendPrompt({ username: req.username }); }}
                              className="px-3 h-8 rounded-xl gradient-premium text-[11px] font-display font-bold text-primary-foreground flex items-center gap-1"
                            >
                              <UserPlus className="w-3 h-3" /> Create Account
                            </motion.button>
                          ) : (
                            <>
                              <motion.button whileTap={{ scale: 0.9 }} onClick={() => handleAcceptFriend(req.id, req.username, req.fromUserId)} className="w-8 h-8 rounded-xl gradient-primary flex items-center justify-center">
                                <Check className="w-3.5 h-3.5 text-primary-foreground" />
                              </motion.button>
                              <motion.button whileTap={{ scale: 0.9 }} onClick={() => ignoreFriendRequest(req.id)} className="w-8 h-8 rounded-xl bg-muted/60 flex items-center justify-center border border-border/30">
                                <X className="w-3.5 h-3.5 text-muted-foreground" />
                              </motion.button>
                            </>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Profile Visitors Modal (Pro) */}
        <AnimatePresence>
          {showVisitors && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100] flex items-end justify-center bg-black/70 backdrop-blur-sm" onClick={() => setShowVisitors(false)}>
              <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
                transition={{ type: "spring", damping: 30, stiffness: 350 }} onClick={(e) => e.stopPropagation()}
                className="w-full max-w-lg rounded-t-3xl bg-card border-t border-border/40 p-6 max-h-[70vh] overflow-y-auto">
                <div className="w-10 h-1 rounded-full bg-muted-foreground/20 mx-auto mb-5" />
                <div className="flex items-center justify-between mb-5">
                  <h3 className="font-display font-bold text-foreground text-lg">Profile Visits</h3>
                  <button onClick={() => setShowVisitors(false)}><X className="w-5 h-5 text-muted-foreground" /></button>
                </div>
                {visitors.length === 0 ? (
                  <div className="text-center py-10"><p className="text-muted-foreground text-sm">No profile visits yet</p></div>
                ) : (
                  <div className="space-y-2">
                    {visitors.map((v) => (
                      <motion.div key={v.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                        className="flex items-center justify-between p-3 rounded-2xl bg-muted/30 border border-border/20">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-full bg-muted/60 flex items-center justify-center overflow-hidden flex-shrink-0">
                            {v.profilePicture ? <img loading="lazy" decoding="async" src={v.profilePicture} alt="" className="w-full h-full object-cover" /> : <User className="w-4 h-4 text-muted-foreground" />}
                          </div>
                          <div className="min-w-0">
                            <p className="font-display font-semibold text-foreground text-sm truncate">{v.username}</p>
                            <p className="text-[10px] text-muted-foreground">Viewed your profile</p>
                          </div>
                        </div>
                        <div className="flex gap-1.5">
                          <motion.button whileTap={{ scale: 0.9 }} onClick={() => handleAddVisitorFriend(v)} className="px-2.5 h-8 rounded-xl gradient-premium flex items-center gap-1 text-primary-foreground text-[10px] font-semibold">
                            <UserPlus className="w-3 h-3" /> Add
                          </motion.button>
                          <motion.button whileTap={{ scale: 0.9 }} onClick={() => handleViewVisitorProfile(v)} className="px-2.5 h-8 rounded-xl bg-muted/60 border border-border/30 text-foreground text-[10px] font-semibold">
                            View
                          </motion.button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Friend Accepted Notification */}
        <AnimatePresence>
          {friendAccepted && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => setFriendAccepted(null)}>
              <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.8, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="w-[85%] max-w-sm rounded-3xl bg-card border border-border/40 p-6 text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-emerald-400" />
                </div>
                <h3 className="font-display font-bold text-foreground text-lg mb-1">You are now friends with</h3>
                <p className="text-gradient-premium font-display font-bold text-xl mb-4">{friendAccepted.username}</p>
                <motion.button whileTap={{ scale: 0.97 }} onClick={handleTapToChat}
                  className="w-full py-3 rounded-xl gradient-premium text-primary-foreground font-display font-semibold text-sm">
                  💬 Tap to Chat
                </motion.button>
                <button onClick={() => setFriendAccepted(null)} className="w-full py-2 mt-2 text-muted-foreground text-xs">
                  Close
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <BottomNav />
      </div>
      <GuestUpgradePrompt
        isOpen={!!guestFriendPrompt}
        onClose={() => setGuestFriendPrompt(null)}
        title={guestFriendPrompt ? `${guestFriendPrompt.username} added you as a friend` : undefined}
        message="Create an account to accept friend requests and start chatting with friends."
      />
    </PageTransition>
  );
};

export default HomePage;
